/**
 * Photos in ICS form text are stored as markers inside the plain-text value:
 * `[[photo:<managed file id>|<caption>]]`. Exports print them as "[Photo n]"
 * and append the pictures; the COMS twin is `routes/Forms/formPhotos.ts`.
 */

const MARKER_SOURCE =
  '\\[\\[photo:([0-9a-fA-F-]{36})(?:\\|([^\\]\\n]*))?\\]\\]';

export type PhotoMarker = {
  fileId: string;
  caption: string;
  /** "Photo 1", numbered in reading order; a repeated photo keeps its number. */
  label: string;
  start: number;
  end: number;
};

function cleanCaption(caption: string): string {
  return caption.replace(/[[\]|\n]/g, ' ').trim();
}

export function photoMarker(fileId: string, caption = ''): string {
  const text = cleanCaption(caption);
  return text ? `[[photo:${fileId}|${text}]]` : `[[photo:${fileId}]]`;
}

export function parsePhotoMarkers(
  text: string | null | undefined
): PhotoMarker[] {
  if (!text) return [];
  const numbers = new Map<string, number>();
  const markers: PhotoMarker[] = [];
  for (const match of text.matchAll(new RegExp(MARKER_SOURCE, 'g'))) {
    const fileId = match[1].toLowerCase();
    if (!numbers.has(fileId)) numbers.set(fileId, numbers.size + 1);
    markers.push({
      fileId,
      caption: (match[2] ?? '').trim(),
      label: `Photo ${numbers.get(fileId)}`,
      start: match.index ?? 0,
      end: (match.index ?? 0) + match[0].length,
    });
  }
  return markers;
}

/** One entry per distinct photo, in first-appearance order. */
export function distinctPhotos(text: string | null | undefined): PhotoMarker[] {
  const seen = new Set<string>();
  return parsePhotoMarkers(text).filter((marker) => {
    if (seen.has(marker.fileId)) return false;
    seen.add(marker.fileId);
    return true;
  });
}

/**
 * Inserts a marker at the selection, on its own line so it never glues onto a
 * word. Returns the new text and where the cursor should land.
 */
export function insertPhotoMarker(
  text: string,
  marker: string,
  selectionStart: number = text.length,
  selectionEnd: number = selectionStart
): { text: string; cursor: number } {
  const before = text.slice(0, selectionStart);
  const after = text.slice(selectionEnd);
  const lead = before.length > 0 && !before.endsWith('\n') ? '\n' : '';
  const trail = after.length > 0 && !after.startsWith('\n') ? '\n' : '';
  const next = `${before}${lead}${marker}${trail}${after}`;
  return { text: next, cursor: before.length + lead.length + marker.length };
}

/** Removes every marker for the photo, and the blank line it leaves behind. */
export function removePhotoMarker(text: string, fileId: string): string {
  const id = fileId.toLowerCase();
  let result = '';
  let last = 0;
  for (const marker of parsePhotoMarkers(text)) {
    if (marker.fileId !== id) continue;
    let start = marker.start;
    let end = marker.end;
    if (text[end] === '\n' && (start === 0 || text[start - 1] === '\n')) {
      end += 1;
    } else if (start > 0 && text[start - 1] === '\n' && end === text.length) {
      start -= 1;
    }
    result += text.slice(last, start);
    last = end;
  }
  return result + text.slice(last);
}
